/* eslint-disable  */
import React, {
  useState,
  useCallback,
  useContext,
  createContext,
  useEffect,
} from 'react';
import api from 'services';
import { useAuth } from 'hooks/auth';
import { useNewToast } from 'hooks/newToast';
import { appWarnings } from 'utils/appWarnings';
import { storage } from 'utils/appLocalStorage';
import { SLOGAN_AGROGER } from 'utils/variaveisGlobais';

const TODAS_AS_FAZENDAS = {
  id: 0,
  nome: 'TODAS AS FAZENDAS',
};

const FazendaContext = createContext({});

const persistFazendaAtivaId = (fazenda) => {
  if (fazenda?.id === undefined || fazenda?.id === null) {
    localStorage.removeItem(storage.fazendaAtivaId);
    return;
  }

  localStorage.setItem(storage.fazendaAtivaId, String(fazenda.id));
};

const resolveFazendaAtiva = (fazendas) => {
  const fazendasAtivas = fazendas.filter(
    (fazenda) => fazenda.situacao !== 'DESATIVADO',
  );
  const defaultFazenda =
    fazendasAtivas.length > 1
      ? TODAS_AS_FAZENDAS
      : fazendasAtivas[0] || fazendas[0];

  const savedId = localStorage.getItem(storage.fazendaAtivaId);
  if (savedId === null) return defaultFazenda;

  const parsedId = Number(savedId);

  if (parsedId === 0 && fazendasAtivas.length > 1) {
    return TODAS_AS_FAZENDAS;
  }

  const fazendaSalva = fazendasAtivas.find((fazenda) => fazenda.id === parsedId);
  if (fazendaSalva) return fazendaSalva;

  localStorage.removeItem(storage.fazendaAtivaId);
  return defaultFazenda;
};

export const FazendaProvider = ({ children }) => {
  const { isAuth, token } = useAuth();
  const { showToast } = useNewToast();

  const [fazendas, setFazendas] = useState([]);
  const [fazendasLoading, setFazendasLoading] = useState(true);
  const [fazenda, setFazenda] = useState({});
  const [fazendaLoading, setFazendaLoading] = useState(false);
  const [fazendaAtiva, setFazendaAtivaState] = useState();
  const [fazendaPrincipal, setFazendaPrincipal] = useState({});
  const [insertFazendaLoading, setInsertFazendaLoading] = useState(false);
  const [deleteFazendaLoading, setDeleteFazendaLoading] = useState(false);
  const [toggleFazendaStatusLoading, setToggleFazendaStatusLoading] =
    useState(false);
  const [isSendFarmCoordinatesLoading, setIsSendFarmCoordinatesLoading] =
    useState(false);
  const [coordinatesOfAllTheFarms, setCoordinatesOfAllTheFarms] = useState([]);
  const [
    isCoordinatesOfAllTheFarmsLoading,
    setIsCoordinatesOfAllTheFarmLoading,
  ] = useState(false);

  const setFazendaAtiva = useCallback((fazendaOrUpdater) => {
    setFazendaAtivaState((prev) => {
      const next =
        typeof fazendaOrUpdater === 'function'
          ? fazendaOrUpdater(prev)
          : fazendaOrUpdater;

      persistFazendaAtivaId(next);
      return next;
    });
  }, []);

  const down = useCallback(() => {
    setFazendas([]);
    setFazendaAtivaState();
  }, []);

  const getFazendas = useCallback(async (filter) => {
    try {
      setFazendasLoading(true);
      const response = await api.fazenda().getFazendas(filter);
      const tempFazenda = response.find((item) => item.principal);
      if (tempFazenda) setFazendaPrincipal(tempFazenda);
      setFazendas(response);
      setFazendasLoading(false);
      return response;
    } catch (errors) {
      setFazendasLoading(false);
      return [];
    }
  }, []);

  const listFazendas = useCallback(async (filter) => {
    try {
      return await api.fazenda().getFazendas(filter);
    } catch {
      return [];
    }
  }, []);

  const getFazendaAtiva = useCallback(async () => {
    try {
      setFazendasLoading(true);
      const response = await api.fazenda().getFazendaAtiva({ coords: 1 });
      setFazendaAtiva(response);
      setFazendasLoading(false);
    } catch (errors) {
      setFazendasLoading(false);
    }
  }, []);

  const getFazenda = useCallback(async (id) => {
    try {
      setFazendaLoading(true);
      const response = await api.fazenda().getFazenda(id);
      setFazenda(response);
      setFazendaLoading(false);
    } catch (errors) {
      setFazendaLoading(false);
    }
  }, []);

  const getCoordinatesOfAllTheFarms = useCallback(async (filter) => {
    try {
      setIsCoordinatesOfAllTheFarmLoading(true);
      const response = await api.fazenda().getCoordinatesOfAllTheFarms(filter);
      setCoordinatesOfAllTheFarms(response);
    } catch {
    } finally {
      setIsCoordinatesOfAllTheFarmLoading(false);
    }
  }, []);

  const insertFazenda = useCallback(async (data) => {
    try {
      setInsertFazendaLoading(true);
      const response = await api.fazenda().insertFazenda(data);
      setFazendas((old) => [...old, response]);
      setInsertFazendaLoading(false);
    } catch (err) {
      setInsertFazendaLoading(false);
    }
  }, []);

  const insertFazendaTutorial = useCallback(
    async (data) => {
      try {
        setInsertFazendaLoading(true);
        const response = await api
          .fazenda()
          .insertFazenda({ ...data, nome: data?.nome?.toUpperCase() });
        getFazendas();
        setFazendaAtiva(response);
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setInsertFazendaLoading(false);
      }
    },
    [getFazendas, showToast],
  );

  const sendFarmCoordinates = useCallback(
    async (data) => {
      try {
        setIsSendFarmCoordinatesLoading(true);
        const dataFormat = {
          latitude: data?.latitude,
          longitude: data?.longitude,
          fazenda: data?.id,
        };
        await api.fazenda().addFazendaCoords(dataFormat);
        getFazenda(data?.id);
        showToast({
          type: 'success',
          message: 'Coordenadas da Fazenda salva com sucesso!',
        });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setIsSendFarmCoordinatesLoading(false);
      }
    },
    [showToast, getFazenda],
  );

  const postFazenda = useCallback(
    async (data) => {
      try {
        setInsertFazendaLoading(true);
        const {
          bairro_corrego,
          cep,
          cidade,
          cnpj_cpf,
          email,
          endereco,
          fazenda,
          ipr,
          switchAvulso,
          telefone_fixo,
          telefone_movel,
          uf,
        } = data;

        const dataFormated = {
          nome: fazenda,
          cnpj: cnpj_cpf,
          fixo: telefone_fixo,
          uf: uf?.uf,
          email,
          telefone: telefone_fixo,
          logradouro: endereco,
          bairro: bairro_corrego,
          cep,
          cidade,
          celi: telefone_movel,
          celii: telefone_movel,
          inscricao: ipr,
          principal: switchAvulso ? 1 : 0,
        };

        await api.fazenda().insertFazenda(dataFormated);
        getFazendas({ coords: 1 });
        showToast({
          type: 'success',
          message: 'Fazenda adicionada com sucesso!',
        });
        setInsertFazendaLoading(false);
      } catch (erro) {
        showToast(appWarnings.Danger(erro));
        setInsertFazendaLoading(false);
        throw new Error(erro);
      }
    },
    [showToast, getFazendas],
  );

  const putFazenda = useCallback(
    async (data) => {
      try {
        setInsertFazendaLoading(true);
        const {
          id,
          bairro_corrego,
          cep,
          cidade,
          cnpj_cpf,
          email,
          endereco,
          fazenda,
          ipr,
          switchAvulso,
          telefone_fixo,
          telefone_movel,
          uf,
        } = data;

        const dataFormated = {
          nome: fazenda,
          cnpj: cnpj_cpf,
          fixo: telefone_fixo,
          uf: uf?.uf,
          email,
          telefone: telefone_fixo,
          logradouro: endereco,
          bairro: bairro_corrego,
          cep,
          cidade,
          celi: telefone_movel,
          celii: telefone_movel,
          inscricao: ipr,
          principal: switchAvulso ? 1 : 0,
        };

        await api.fazenda().putFazenda(id, dataFormated);
        getFazendas();
        showToast({
          type: 'success',
          message: 'Fazenda editada com sucesso!',
        });
        setInsertFazendaLoading(false);
      } catch (erro) {
        showToast(appWarnings.Danger(erro));
        setInsertFazendaLoading(false);
        throw new Error(erro);
      }
    },
    [showToast, getFazendas],
  );

  const delFazenda = useCallback(
    async (id) => {
      try {
        setDeleteFazendaLoading(true);
        await api.fazenda().delFazenda(id);
        setFazendas((old) => old.filter((item) => item.id !== id));
        showToast({
          type: 'success',
          message: 'Fazenda removida com sucesso!',
        });
        setDeleteFazendaLoading(false);
      } catch (erro) {
        setDeleteFazendaLoading(false);
        showToast(appWarnings.Danger(erro));
        throw new Error(erro);
      }
    },
    [showToast],
  );

  const toggleFazendaStatus = useCallback(
    async (id) => {
      try {
        setToggleFazendaStatusLoading(true);
        const response = await api.fazenda().toggleFazendaStatus(id);
        await getFazendas({ coords: 1 });
        showToast({
          type: 'success',
          message: `Fazenda ${
            response?.situacao === 'DESATIVADO' ? 'desativada' : 'ativada'
          } com sucesso!`,
        });
        setToggleFazendaStatusLoading(false);
        return response;
      } catch (erro) {
        setToggleFazendaStatusLoading(false);
        showToast(appWarnings.Danger(erro));
        throw new Error(erro);
      }
    },
    [showToast, getFazendas],
  );

  useEffect(() => {
    if (!!isAuth && token !== 'initial') {
      getFazendas({ coords: 1 });
    } else {
      down();
    }
  }, [isAuth, getFazendas, down, token]);

  useEffect(() => {
    if (fazendaAtiva || !fazendas?.length) return;

    setFazendaAtiva(resolveFazendaAtiva(fazendas));
  }, [fazendas, fazendaAtiva, setFazendaAtiva]);

  useEffect(() => {
    if (fazendaAtiva?.nome) {
      document.title = `${
        fazendaAtiva?.nome.toUpperCase() || ''
      } | ${SLOGAN_AGROGER}`;
    }

    return () => (document.title = SLOGAN_AGROGER);
  }, [fazendaAtiva]);

  return (
    <FazendaContext.Provider
      value={{
        getFazendaAtiva,
        getFazendas,
        listFazendas,
        fazendas,
        deleteFazendaLoading,
        fazendasLoading,
        insertFazendaLoading,
        getFazenda,
        insertFazenda,
        insertFazendaTutorial,
        fazenda,
        fazendaLoading,
        fazendaAtiva,
        setFazendaPrincipal,
        setFazendaAtiva,
        sendFarmCoordinates,
        isSendFarmCoordinatesLoading,
        postFazenda,
        delFazenda,
        putFazenda,
        toggleFazendaStatus,
        toggleFazendaStatusLoading,
        fazendaPrincipal,
        getCoordinatesOfAllTheFarms,
        coordinatesOfAllTheFarms,
        isCoordinatesOfAllTheFarmsLoading,
      }}
    >
      {children}
    </FazendaContext.Provider>
  );
};

export function useFazenda() {
  const context = useContext(FazendaContext);

  if (!context)
    throw new Error('useFazenda must be used within an AuthProvider');

  return context;
}
